/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 *
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 *
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the batch management page component
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

import { Theme, makeStyles } from '@material-ui/core/styles';

import { IRequestState, RequestState } from '@ngt/request-utilities';

import { Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, Grid, MenuItem, Paper } from '@material-ui/core';

import { Column, Row } from 'react-table';

import CircularProgress from '@material-ui/core/CircularProgress';

import { DateTime } from 'luxon';

import * as Yup from 'yup';

import { Field, FormRenderProps } from 'react-final-form';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */


import { CollapsibleTable } from '@ngt/online-patient-management-ui';
import { useSnackbar } from 'notistack';
import * as Dtos from '../../api/Neon.Dtos';
import DrugBreadcrumbs from '../../components/Breadcrumbs/DrugBreadcrumbs';
import { AsyncForm } from '../../components/FinalForm/AsyncForm';
import { CheckboxWrapper } from '../../components/FinalForm/FinalFormControls/CheckboxWrapper';
import DatePickerWrapper from '../../components/FinalForm/FinalFormControls/DatePickerWrapper';
import { SelectWrapper } from '../../components/FinalForm/FinalFormControls/SelectWrapper';
import TextFieldWrapper from '../../components/FinalForm/FinalFormControls/TextFieldWrapper';
import useAsyncFunction from '../../hooks/useAsyncFunction';
import { BatchActions, useBatch } from '../../store/modules/drugmanagement/batch';
import { useBatches } from '../../store/modules/drugmanagement/batches';
import { useDrugs } from '../../store/modules/drugmanagement/drugs';
import PageLayout from 'src/components/Layout/PageLayout';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces
 * ---------------------------------------------------------------------------------
 */

interface IBatchManagementProps {
}

interface IBatchModalProps {
    batch: Dtos.Batch | null;
    modalOpen: boolean;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    onCloseClick?: () => void;
    onFormSave?: () => void;
}

interface IBatchForm extends Partial<Dtos.Batch> {
}

interface IBatchFormProps extends FormRenderProps<Dtos.Batch> {
    drugs: Dtos.Drug[] | null;
    drugLoadState: IRequestState<Dtos.ResponseStatus>;
    setModalOpen: (open: boolean) => void;
    onFormSave?: () => void;
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles<Theme>(theme => ({
    container: {
        padding: theme.spacing(3)
    },
    buttonSet: {
        paddingBottom: theme.spacing(1),
        textAlign: 'right'
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Hooks
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const useBatchColumns = (batches: Dtos.Batch[] | null) => {
    const columns = React.useMemo(() => {
        const cols: Array<Column<Dtos.Batch>> = [
            {
                Header: 'Batch Number',
                accessor: 'batchNo',
                minWidth: 155,
                maxWidth: 155
            },
            {
                Header: 'Drug Type',
                id: 'drugName',
                accessor: b => b.drug.drugName,
                minWidth: 155,
                maxWidth: 155
            },
            {
                id: 'dateExpiration',
                Header: 'Date of Expiration',
                accessor: batch => batch.dateExpiration,
                Cell: (props: any) => DateTime.fromISO(props.cell.value).toFormat('dd/MM/yyyy'),
                minWidth: 210,
                maxWidth: 210
            }
        ]

        return cols;
    }, []);

    return columns;
};

const formSubscriptionParams = {
    submitting: true,
    pristine: false,
    submitError: true,
    dirtySinceLastSubmit: true,
    hasValidationErrors: true,
    valid: true,
    validating: true,
    errors: true,
    form: true
};

const validation = Yup.object().shape<IBatchForm>({
    drugId: Yup.number().required(),
    batchNo: Yup.string().required(),
    dateExpiration: Yup.string().required(),
    available: Yup.boolean()
});

const BatchForm: React.FunctionComponent<IBatchFormProps> = ({
    drugs,
    drugLoadState,
    setModalOpen,
    onFormSave,
    ...formRenderProps
}) => {
    const { enqueueSnackbar } = useSnackbar();

    const onOkClick = React.useCallback(async (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        try {
            const response = await formRenderProps.handleSubmit();

            if (response) {
                setModalOpen(false);

                if (onFormSave) {
                    onFormSave();
                }
            }
        }
        catch (error: any) {
            enqueueSnackbar('An error occurred while attempting to save the batch.', { variant: 'error' });
        }
    }, [setModalOpen, enqueueSnackbar, formRenderProps, onFormSave]);

    const onCloseClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setModalOpen(false);
    }, [setModalOpen]);

    const drugItems = React.useMemo(() => {
        if (!drugs || drugs.length === 0) {
            return [];
        }
        else {
            return drugs.map(drug => {
                return <MenuItem
                    key={drug.id}
                    value={drug.id}
                >
                    {drug.drugName}
                </MenuItem>;
            });
        }
    }, [drugs]);

    const formDisabled = React.useMemo(() => {
        return formRenderProps.submitting;
    }, [formRenderProps.submitting]);

    const drugDisabled = React.useMemo(() => {
        return (drugs && drugs.length === 1) || formDisabled;
    }, [drugs, formDisabled]);

    return (
        <>
            <DialogContent>
                <Grid container alignItems="flex-start" spacing={2}>
                    <Grid item xs={12}>
                        {
                            drugLoadState && drugLoadState.state && drugLoadState.state === RequestState.Pending ?
                                <CircularProgress /> :
                                <Field
                                    name="drugId"
                                    component={SelectWrapper}
                                    type="text"
                                    disabled={drugDisabled}
                                    label="Drug"
                                    formControlProps={{
                                        fullWidth: true
                                    }}
                                >
                                    {drugItems}
                                </Field>
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="batchNo"
                            component={TextFieldWrapper}
                            type="text"
                            label="Batch Number"
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Field
                            fullWidth
                            name="dateExpiration"
                            component={DatePickerWrapper}
                            label="Expiry Date"
                            includeOffset={true}
                            removeTime={true}
                            disabled={formDisabled}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Field
                                        name="available"
                                        component={CheckboxWrapper}
                                        label="Available"
                                        defaultValue={true}
                                        disabled={formDisabled}
                                    />
                                }
                                label="Available"
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseClick} color="primary" disabled={formDisabled}>
                    Cancel
                </Button>
                <Button onClick={onOkClick}
                    color="primary" autoFocus disabled={formDisabled}>
                    OK
                </Button>
            </DialogActions>
        </>
    );
};

const BatchModal: React.FunctionComponent<IBatchModalProps> = ({
    batch,
    modalOpen,
    setModalOpen,
    drugs,
    drugLoadState,
    onCloseClick,
    onFormSave
}) => {
    const batchForm = React.useCallback((props: FormRenderProps<Dtos.Batch>) => {
        return (
            <BatchForm
                drugs={drugs}
                drugLoadState={drugLoadState}
                setModalOpen={setModalOpen}
                onFormSave={onFormSave}
                {...props}
            />
        );
    }, [drugs, drugLoadState, setModalOpen, onFormSave]);

    const asyncSave = useAsyncFunction(BatchActions.save, BatchActions.saveSuccess, BatchActions.saveFailure);

    return (
        <>
            <Dialog
                open={modalOpen}
                onClose={onCloseClick}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    Save Batch
                </DialogTitle>
                <AsyncForm
                    initialValues={batch}
                    validate={validation}
                    onSubmit={asyncSave}
                    formSubscription={formSubscriptionParams}
                    validationDebounceTimeout={200}
                    component={batchForm}
                />
            </Dialog>
        </>
    );
}

const BatchManagement: React.FunctionComponent<IBatchManagementProps> = () => {
    const classes = useStyles();

    const [drugs, drugLoadState, drugActions] = useDrugs();

    const [batches, batchesLoadState, batchesActions] = useBatches();

    const [batch, batchLoadState, , batchActions] = useBatch();

    const [modalOpen, setModalOpen] = React.useState(false);

    const [batchForm, setBatch] = React.useState<Dtos.Batch | null>(null);

    React.useEffect(() => {
        drugActions.load();

        return () => {
            drugActions.clear();
        }
    }, [drugActions]);

    React.useEffect(() => {
        batchesActions.load();

        return () => {
            batchesActions.clear();
        }
    }, [batchesActions]);

    React.useEffect(() => {
        if (batchLoadState.state === RequestState.Success) {
            setBatch(batch);

            setModalOpen(true);
        }
    }, [batchLoadState.state, batch]);

    const onFormSave = React.useCallback(() => {
        batchActions.clear();
        batchesActions.load();
    }, [batchActions, batchesActions]);

    const addBatchClick = React.useCallback(() => {
        setBatch(new Dtos.Batch({ drugId: 1, available: true }));

        setModalOpen(true);
    }, [setModalOpen]);

    const onRowClick = React.useCallback((row: Row<Dtos.Batch>) => {
        if (row &&
            row.original) {
            batchActions.clear();
            batchActions.loadById(row.original.id!);
        }
    }, [batchActions]);

    const batchColumns = useBatchColumns(batches);

    //this is here for general cleanup in case something is loaded into redux and never reset
    React.useEffect(() => {
        return () => {
            batchActions.clear();
        }
    }, [batchActions]);

    return (
        <>
            <BatchModal
                batch={batchForm}
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                drugs={drugs}
                drugLoadState={drugLoadState}
                onFormSave={onFormSave}
            />
            <PageLayout
                breadcrumbs={
                    <DrugBreadcrumbs
                        showInstitution={true}
                    />
                }
                heading="Batches"
            >
                <Paper
                    className={classes.container}
                >
                    <div
                        className={classes.buttonSet}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={addBatchClick}
                        >
                            Add Batch
                        </Button>
                    </div>
                    <CollapsibleTable
                        title="Batch"
                        loadState={batchesLoadState}
                        data={batches}
                        columns={batchColumns}
                        onRowClick={onRowClick}
                    />
                </Paper>
            </PageLayout>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Default Export
 * ---------------------------------------------------------------------------------
 */
export default BatchManagement;